import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import TaskSubNav from '../components/TaskSubNav';
import TaskAttachments, { uploadPendingAttachments } from '../components/TaskAttachments';
import { showToast } from '../components/Toast';
import { DSBackLink, DSButton, DSInput, DSTextarea, DSCard, DSIcon } from '../design-system/primitives';
import './taskManagementShared.css';

function TaskSuggest() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [wantsToDoIt, setWantsToDoIt] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pendingScreenshots, setPendingScreenshots] = useState([]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await apiService.createTaskSuggestion({ title, description, wantsToDoIt });
      try {
        await uploadPendingAttachments('suggestion', response.data.id, pendingScreenshots);
      } catch (uploadErr) {
        // The suggestion is saved; screenshots can be added again from My Suggestions.
        showToast(uploadErr.response?.data?.error || 'Suggestion sent, but the screenshots could not be attached. Add them from My Suggestions.', 'error');
      }
      navigate('/tasks/my-suggestions', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tm-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>
      <div className="tm-section">
        <h1>Suggest a Task</h1>
        <p>Got something that needs doing around the club? Flag it here and the team will review it.
          No pressure, you don't have to do it yourself!</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="tm-form-group">
            <DSInput label="Task Title *" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="tm-form-group">
            <DSTextarea label="Description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="tm-form-group">
            <TaskAttachments pendingFiles={pendingScreenshots} onPendingChange={setPendingScreenshots} />
          </div>
          <div className="tm-form-group">
            <label>Your Involvement</label>
            <DSCard
              variant="elevated"
              bgVariant="default"
              borderVariant="subtle"
              cornerVariant="lg"
              className={`tm-radio-card ${wantsToDoIt ? 'selected' : ''}`}
              onClick={() => setWantsToDoIt(true)}
            >
              <input type="radio" checked={wantsToDoIt} onChange={() => setWantsToDoIt(true)} />
              <div>
                <strong>I'd like to do this myself</strong>
                <div>If approved, a Task Delegate can assign it directly to you.</div>
              </div>
            </DSCard>
            <DSCard
              variant="elevated"
              bgVariant="default"
              borderVariant="subtle"
              cornerVariant="lg"
              className={`tm-radio-card ${!wantsToDoIt ? 'selected' : ''}`}
              onClick={() => setWantsToDoIt(false)}
            >
              <input type="radio" checked={!wantsToDoIt} onChange={() => setWantsToDoIt(false)} />
              <div>
                <strong>Just flagging it</strong>
                <div>Goes into the open pool if approved, so anyone can pick it up.</div>
              </div>
            </DSCard>
          </div>
          <DSButton type="submit" appearance="solid" tone="primary" disabled={submitting} leadingIcon={<DSIcon name="note" />}>{submitting ? 'Submitting...' : 'Submit Suggestion'}
          </DSButton>
        </form>
      </div>
      <TaskSubNav />
    </div>
  );
}

export default TaskSuggest;
